/**
 * 효능·의료 표현 검사 — 일반 상품의 상품명·검색태그에 «통증 완화·치료·염증» 같은 의료 효능을 쓰면 뺀다(PR261017-D188A7 «라텍스 쿠션 깔창 통증 완화»).
 * 쿠팡 연관 키워드(«통증 완화 깔창»)를 AI 가 옮겨 오는데, 의료기기가 아닌 상품의 질병·통증 효능 표시는 법 위반 소지가 있다.
 * 등록 카테고리가 의료기기·의료용품일 때만 남긴다. 카테고리를 모르면 일반 상품으로 본다.
 * 의존성 없는 순수 파일 — 단위시험 __tests__/seoEfficacyGuard.test.mjs
 */

const MEDICAL_CATEGORY_RE = /의료/;
// 몸 부위 — 효능 말 앞에 붙어 있어도 남긴다(«무릎 통증 보호대» → «무릎 보호대», #2017 둘째 반려)
const BODY = '(?:발바닥|발뒤꿈치|뒤꿈치|발목|발|무릎|허리|관절|척추|어깨|목|손목|골반|근육)';
const BODY_RE = new RegExp(`^${BODY}$`);
// 낱말 경계 — 효능 말이 낱말 일부면(«염증성») 조각을 남기지 않게 낱말 통째로 뺀다(#2017 둘째 반려)
const SEP_RE = /[\s,/·()[\]+&]/;
// 효능 말 뒤에 붙은 조각·주장 말 — 남기지 않고 같이 뺀다(«염증성»의 «성» · «질환 없는»의 «없는», #2017 셋째 반려)
const TAIL_RE = /^(?:성|용|적|에|엔|에도|효과|효과용|없는|없이|걱정|걱정없는|방지|예방)$/;
// «통풍»은 환기(통풍구)와 같은 글자라, «디스크»는 원판(하드디스크)과 같은 글자라 넣지 않는다. «충격 완화»(쿠션)는 효능 주장이 아니다
// «교정»도 넣지 않는다 — «자세 교정»·«체형 교정»·«교정 깔창»은 상품 종류(자세교정기·교정 밴드) 이름이다(#2017 반려). 빼는 것은 «통증 완화» 같은 효과 주장·병 이름뿐
const EFFICACY_SRC = [
  '통증\\s*(?:완화|감소|해소|개선|예방|케어|억제)?',
  '(?:족저)?근막염|무지외반증?|관절염|신경통|하지정맥류',
  '치료(?:용|효과)?|완치|질환|질병|염증|무좀|혈액\\s*순환|혈행\\s*개선|의료용'
].join('|');
const EFFICACY_RE = new RegExp(EFFICACY_SRC, 'g');

function tidy(s) {
  return String(s)
    .replace(/\s*[,/·]\s*(?=[,/·]|$)/g, '')
    .replace(/^[\s,/·]+/, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** 등록 카테고리가 의료기기·의료용품인가 */
export function isMedicalCategory(categoryPath) {
  return MEDICAL_CATEGORY_RE.test(String(categoryPath || ''));
}

/**
 * 상품명에서 효능·의료 표현을 뺀다.
 * @param {string} productName
 * @param {string} categoryPath - 등록 카테고리 이름 경로(모르면 빈 값)
 * @returns {{ name: string, removed: string[] }}
 */
export function dropEfficacyClaims(productName, categoryPath) {
  if (!productName || typeof productName !== 'string') return { name: productName, removed: [] };
  if (isMedicalCategory(categoryPath)) return { name: productName, removed: [] };
  const removed = [];
  let out = '';
  let last = 0;
  EFFICACY_RE.lastIndex = 0;
  let m;
  while ((m = EFFICACY_RE.exec(productName))) {
    const a = m.index;
    let b = a + m[0].length;
    while (b > a && /\s/.test(productName[b - 1])) b--; // «통증 » 뒤 빈칸은 효능 말이 아니다
    if (b === a) { EFFICACY_RE.lastIndex = a + 1; continue; }
    let ws = a;
    while (ws > last && !SEP_RE.test(productName[ws - 1])) ws--;
    let we = b;
    while (we < productName.length && !SEP_RE.test(productName[we])) we++;
    const prefix = productName.slice(ws, a);
    const suffix = productName.slice(b, we);
    // 낱말 앞이 몸 부위(또는 없음)면 남기고, 뒤가 상품 이름이면 남긴다(«무릎통증보호대» → «무릎보호대»). 뒤가 조각(«성»)이면 같이 뺀다
    const keepPrefix = !prefix || BODY_RE.test(prefix);
    const keepSuffix = keepPrefix && !!suffix && !TAIL_RE.test(suffix);
    const cut = keepPrefix ? a : ws;
    let end = keepSuffix ? b : we;
    if (!keepSuffix) {
      // 띄어 쓴 주장 말(«질환 없는»의 «없는»)도 같이 뺀다
      let ns = end;
      while (ns < productName.length && /\s/.test(productName[ns])) ns++;
      let ne = ns;
      while (ne < productName.length && !SEP_RE.test(productName[ne])) ne++;
      if (ne > ns && TAIL_RE.test(productName.slice(ns, ne))) end = ne;
    }
    removed.push(productName.slice(cut, end).trim());
    // 몸 부위와 상품 이름 사이만 뺐으면 붙여 둔다(«무릎보호대»)
    out += productName.slice(last, cut) + (prefix && keepSuffix ? '' : '\uE000');
    last = end;
    EFFICACY_RE.lastIndex = end;
  }
  const name = (out + productName.slice(last)).replace(/\s*\uE000\s*[,/·]?/g, ' '); // 뺀 자리 뒤의 쉼표·슬래시도 같이 뺀다
  if (!removed.length) return { name: productName, removed };
  const cleaned = tidy(name);
  if (!cleaned) return { name: productName, removed: [] };
  return { name: cleaned, removed };
}

/**
 * 검색태그에서 효능·의료 표현이 든 태그를 통째로 뺀다(«통증 완화 깔창»·«발바닥 염증»).
 * @param {string[]} keywords
 * @param {string} categoryPath
 * @returns {{ keywords: string[], removed: string[] }}
 */
export function dropEfficacyClaimTags(keywords, categoryPath) {
  if (!Array.isArray(keywords)) return { keywords, removed: [] };
  if (isMedicalCategory(categoryPath)) return { keywords, removed: [] };
  const re = new RegExp(EFFICACY_SRC);
  const removed = [];
  const out = keywords.filter(k => {
    const bad = re.test(String(k || ''));
    if (bad) removed.push(String(k));
    return !bad;
  });
  if (!removed.length) return { keywords, removed };
  return { keywords: out, removed };
}

export default { isMedicalCategory, dropEfficacyClaims, dropEfficacyClaimTags };
